// src/components/AppointmentScheduler.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getNextBusinessDays } from '../utils/dateUtils';

const API_URL = 'https://sae-scheduling-app-react-backend-dev.azurewebsites.net';

const SchedulingUnavailable: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="max-w-md w-full bg-white rounded-xl shadow-md p-8">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Schedule</h3>
        <p className="text-gray-500">
          I'm sorry, this link has expired. If you need to schedule (or reschedule) your intro call, please reach out to your Case Manager for assistance.
        </p>
      </div>
    </div>
  </div>
);

interface TimeSlot {
  time: string;
  available: boolean;
}

interface AvailabilityResponse {
  slots: TimeSlot[];
  busyPeriods: any[];
  timezone: string;
}


interface Confirmation {
  staffEmail: string;
  date: string;
  time: string;
  clientName: string;
  clientEmail: string;
}

const AppointmentScheduler: React.FC = () => {
  const { staffEmail, matterId } = useParams<{ staffEmail?: string; matterId?: string }>();
  
  // State declarations
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [matterIdRef, setMatterIdRef] = useState('');
  const [matActTrackingId, setMatActTrackingId] = useState('');
  const [staffSfId, setStaffSfId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [clientMobilePhone, setClientMobilePhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [isMatterLoading, setIsMatterLoading] = useState(false);
  const [matterError, setMatterError] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [staffTimezone, setStaffTimezone] = useState<string>('');
  const TIMEZONE_ABBREVIATIONS: { [key: string]: string } = {
    'Eastern Standard Time': 'EST',
    'Central Standard Time': 'CST',
    'Mountain Standard Time': 'MST',
    'Pacific Standard Time': 'PST'
  };

  useEffect(() => {
    const fetchMatterData = async () => {
      if (!matterId) return;

      setIsMatterLoading(true);
      setMatterError(null);

      try {
        const response = await fetch(`${API_URL}/api/matter/${matterId}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch matter data');
        }

        // Check if scheduling is allowed
        if (data.introCallScheduling !== 'true') {
          setMatterError('scheduling_not_available');
          return;
        }

        setEmail(data.staffEmail);
        setStaffName(data.staffName);
        setClientName(data.clientName);
        setClientEmail(data.clientEmail);
        setClientPhone(data.clientPhone);
        setClientMobilePhone(data.clientMobilePhone);
        setReferenceNumber(data.referenceNumber);
        setMatterIdRef(data.matterIdRef);
        setStaffSfId(data.staffSfId);
        setMatActTrackingId(data.matActTrackingId);

        const nextBusinessDays = getNextBusinessDays(data.createdDate);
        setAvailableDates(nextBusinessDays);
      } catch (err) {
        setMatterError(err instanceof Error ? err.message : 'Failed to fetch matter data');
        console.error('Error fetching matter data:', err);
      } finally {
        setIsMatterLoading(false);
      }
    };

    fetchMatterData();
  }, [matterId]);

  useEffect(() => {
    if (staffEmail) {
      setEmail(decodeURIComponent(staffEmail));
    }
  }, [staffEmail]);

  const formatTime = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    const time = new Date(dateString).toLocaleTimeString(undefined, options);
    const zoneAbbr = TIMEZONE_ABBREVIATIONS[staffTimezone] || staffTimezone;
    return `${time} ${zoneAbbr}`;
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString(undefined, options);
  };

  const addMinutesToTimeString = (timeString: string, minutes: number) => {
    const date = new Date(timeString);
    const newDate = new Date(date.getTime() + minutes * 60000);
    
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    const day = String(newDate.getDate()).padStart(2, '0');
    const hours = String(newDate.getHours()).padStart(2, '0');
    const mins = String(newDate.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${mins}:00`;
  };

  const renderDateSelect = () => {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Available Date
        </label>
        <select
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
        >
          <option value="">Select a date</option>
          {availableDates.map((availableDate) => {
            const dateString = availableDate.toISOString().split('T')[0];
            return (
              <option key={dateString} value={dateString}>
                {formatDate(dateString)}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  const fetchAvailability = async () => {
    if (!date) {
      setError('Please select a date');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, date }),
      });

      const data: AvailabilityResponse = await response.json();

      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }

      if (!data.slots || data.slots.length === 0) {
        setError('No available time slots found for this date. Please try another date.');
        return;
      }

      setStaffTimezone(data.timezone);
      setAvailableSlots(data.slots);
      setStep(2);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch availability';
      setError(errorMessage);
      console.error('Error in fetchAvailability:', err);
    } finally {
      setLoading(false);
    }
  };


  const confirmAppointment = async () => {
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const endTime = addMinutesToTimeString(selectedSlot.time, 30);
      
      const requestBody = {
        email,
        startTime: selectedSlot.time,
        endTime,
        clientName,
        clientEmail,
        clientPhone,
        clientMobilePhone,
        referenceNumber,
        staffName,
        matterIdRef,
        matterId,
        staffSfId,
        matActTrackingId,
        staffTimezone,
      };

      //console.log('Request Body:', requestBody);

      const response = await fetch(`${API_URL}/api/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to book appointment');
      }

      setConfirmation({
        staffEmail: email,
        date: date,
        time: selectedSlot.time,
        clientName,
        clientEmail,
      });
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  if (isMatterLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 mx-auto text-yellow-400 mb-4" />
          <p className="text-gray-600">Loading matter information...</p>
        </div>
      </div>
    );
  }

  if (matterError) {
    return <SchedulingUnavailable />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/mm-logo.png"
            alt="Morgan & Morgan"
            className="h-36 mx-auto mb-2"
          />
          <p className="text-gray-900">
            Schedule Your Intro Call
          </p>
          <p className="text-sm text-gray-500 mt-2">
            I would like to introduce myself and kick-off your case right away! Please schedule a time for us to speak, and I will call you at the phone number on file.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Step 1: Date Selection */}
        {step === 1 && (
          <div className="space-y-6">
            {renderDateSelect()}
            <button
              onClick={fetchAvailability}
              disabled={!date || loading}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="animate-spin h-5 w-5" />
              ) : (
                'Check Availability'
              )}
            </button>
          </div>
        )}

        {/* Step 2: Time Slot Selection */}
        {step === 2 && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">
              Available Time Slots for {formatDate(date)}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {availableSlots.map((slot, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedSlot(slot)}
                  className={`p-4 rounded-md text-center transition-colors duration-200 ${
                    selectedSlot === slot
                      ? 'bg-yellow-400 text-black'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  {formatTime(slot.time)}
                </button>
              ))}
            </div>

            {availableSlots.length === 0 && (
              <p className="text-center text-gray-500">
                No available time slots found for this date. Please try another date.
              </p>
            )}

            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setStep(1);
                  setSelectedSlot(null);
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-black font-bold py-2 px-4 rounded-md transition-colors duration-200"
              >
                Back
              </button>
              <button
                onClick={confirmAppointment}
                disabled={!selectedSlot || loading}
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="animate-spin h-5 w-5" />
                ) : (
                  'Confirm Appointment'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && confirmation && (
          <div className="text-center space-y-4">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Appointment Confirmed!
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              You will receive a calendar invite shortly to the email address provided at case opening.
            </p>
            <div className="text-sm text-gray-500">
              <p>Date: {formatDate(confirmation.date)}</p>
              <p>Time: {formatTime(confirmation.time)}</p>
            </div>
            <button
              onClick={() => window.location.href = "/"}
              className="mt-4 bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2 px-4 rounded-md transition-colors duration-200"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentScheduler;